import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Eye, EyeOff, CheckCircle2, Building2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import authService from '../../services/auth.service';

const ChangePasswordPage = () => {
  const navigate    = useNavigate();
  const { user, updateUser } = useAuth();

  const [form, setForm] = useState({
    current_password: '',
    new_password:     '',
    confirm_password: '',
  });
  const [showCurrent, setShowCurrent]   = useState(false);
  const [showNew,     setShowNew]       = useState(false);
  const [error,       setError]         = useState('');
  const [loading,     setLoading]       = useState(false);
  const [success,     setSuccess]       = useState(false);

  // Password strength checker
  const getStrength = (pwd: string): { score: number; label: string; color: string } => {
    let score = 0;
    if (pwd.length >= 8)              score++;
    if (/[A-Z]/.test(pwd))            score++;
    if (/[0-9]/.test(pwd))            score++;
    if (/[^A-Za-z0-9]/.test(pwd))    score++;

    if (score <= 1) return { score, label: 'Weak',   color: 'bg-red-400' };
    if (score === 2) return { score, label: 'Fair',   color: 'bg-yellow-400' };
    if (score === 3) return { score, label: 'Good',   color: 'bg-blue-400' };
    return               { score, label: 'Strong', color: 'bg-green-500' };
  };

  const strength = getStrength(form.new_password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!form.current_password || !form.new_password || !form.confirm_password) {
      setError('All fields are required.');
      return;
    }
    if (form.new_password.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('New passwords do not match.');
      return;
    }
    if (form.current_password === form.new_password) {
      setError('New password must be different from current password.');
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword({
        current_password: form.current_password,
        new_password:     form.new_password,
      });

      setSuccess(true);

      // Update local user state — must_change_pwd is now false
      if (user) {
        updateUser({ ...user, must_change_pwd: false });
      }

      // Redirect to dashboard after 1.5 seconds
      setTimeout(() => navigate('/', { replace: true }), 1500);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100
                    flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16
                          bg-blue-600 rounded-2xl shadow-lg mb-4">
            <Building2 size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Set Your Password</h1>
          <p className="text-gray-500 text-sm mt-1">
            Welcome, {user?.full_name}. Please set a new password to continue.
          </p>
        </div>

        {/* Card */}
        <div className="card">
          <div className="card-body">

            {/* Success state */}
            {success ? (
              <div className="text-center py-6">
                <CheckCircle2 size={48} className="text-green-500 mx-auto mb-3" />
                <p className="text-gray-800 font-medium">Password changed successfully!</p>
                <p className="text-gray-500 text-sm mt-1">Redirecting to dashboard...</p>
              </div>
            ) : (
              <>
                <div className="alert-info mb-4">
                  <KeyRound size={16} />
                  <span>Your password must be changed before you can continue.</span>
                </div>

                {error && (
                  <div className="alert-error mb-4">
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">

                  {/* Current Password */}
                  <div>
                    <label className="form-label">Current Password</label>
                    <div className="relative">
                      <input
                        type={showCurrent ? 'text' : 'password'}
                        className="form-input pr-10"
                        placeholder="Enter current password"
                        value={form.current_password}
                        onChange={e => setForm({ ...form, current_password: e.target.value })}
                        disabled={loading}
                      />
                      <button type="button"
                        onClick={() => setShowCurrent(!showCurrent)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="form-label">New Password</label>
                    <div className="relative">
                      <input
                        type={showNew ? 'text' : 'password'}
                        className="form-input pr-10"
                        placeholder="Minimum 8 characters"
                        value={form.new_password}
                        onChange={e => setForm({ ...form, new_password: e.target.value })}
                        disabled={loading}
                      />
                      <button type="button"
                        onClick={() => setShowNew(!showNew)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>

                    {/* Password strength bar */}
                    {form.new_password && (
                      <div className="mt-2">
                        <div className="flex gap-1 mb-1">
                          {[1, 2, 3, 4].map(i => (
                            <div key={i}
                              className={`h-1 flex-1 rounded-full transition-colors ${
                                i <= strength.score ? strength.color : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          Strength: <span className="font-medium">{strength.label}</span>
                          {' '}— Use uppercase, numbers and symbols for stronger password
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="form-label">Confirm New Password</label>
                    <input
                      type="password"
                      className={`form-input ${
                        form.confirm_password && form.confirm_password !== form.new_password
                          ? 'border-red-400 focus:ring-red-500'
                          : ''
                      }`}
                      placeholder="Re-enter new password"
                      value={form.confirm_password}
                      onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                      disabled={loading}
                    />
                    {form.confirm_password && form.confirm_password !== form.new_password && (
                      <p className="form-error">Passwords do not match</p>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    className="btn-primary w-full justify-center py-2.5 mt-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4
                                        border-b-2 border-white" />
                        Changing...
                      </>
                    ) : (
                      <>
                        <KeyRound size={16} />
                        Change Password
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;